import JSZip from "jszip";

export type GenerateBillCase = {
  date: string;
  caseId: string;
  caseName: string;
  category: string;
  toothNumbers: string;
  units: number;
  pricePerUnit: number;
  total: number;
  status: string;
  unitsPrevBilled: number;
  pendingUnits: number;
  labToken: string;
  /** False when units were not employee-marked — row is highlighted in Excel. */
  unitsMarked?: boolean;
  /** True when unit-allocator notes were appended to tooth numbers — green highlight. */
  hasUnitsNotes?: boolean;
};

export type GenerateBillLabSheet = {
  key: string;
  sheetName: string;
  invoiceName: string;
  currency: string;
  isElementDigital: boolean;
  cases: GenerateBillCase[];
};

export type GenerateBillPayload = {
  month: number;
  year: number;
  monthName: string;
  labCount: number;
  caseCount: number;
  labs: GenerateBillLabSheet[];
};

const HEADER_FILL = "93CDDB";
const OTHERS_FILL = "FFF2CC"; // yellow highlight for category Others
const UNMARKED_FILL = "FFC7CE"; // red highlight for unmarked units
const NOTES_FILL = "C6EFCE"; // green highlight when units notes are present

const sanitizeSheetName = (name: string, used: Set<string>) => {
  // Excel forbids [] in sheet names — use fullwidth brackets so "[8] ED" stays visible.
  let base = String(name || "Sheet")
    .replace(/\[/g, "［")
    .replace(/\]/g, "］")
    .replace(/[\\/?*:]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 31);
  if (!base) base = "Sheet";
  let candidate = base;
  let i = 2;
  while (used.has(candidate.toLowerCase())) {
    const suffix = ` (${i})`;
    candidate = `${base.slice(0, Math.max(1, 31 - suffix.length))}${suffix}`;
    i += 1;
  }
  used.add(candidate.toLowerCase());
  return candidate;
};

const round2 = (n: number) => Math.round(Number(n || 0) * 100) / 100;

const xmlEscape = (value: string) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const colLetter = (index: number) => {
  let n = index + 1;
  let out = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    out = String.fromCharCode(65 + rem) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
};

/** Style ids from stylesXml below */
const S = {
  title: 1,
  subtitle: 2,
  header: 3,
  cell: 4,
  cellNum: 5,
  grandHeader: 6,
  footer: 7,
  footerNum: 8,
  cellWarn: 9,
  cellNumWarn: 10,
  cellLeft: 11,
  cellNumRight: 12,
  footerLeft: 13,
  footerRight: 14,
  footerNumRight: 15,
  cellRight: 16,
  cellUnmarked: 17,
  cellNumUnmarked: 18,
  cellNotes: 19,
  cellNumNotes: 20,
} as const;

const stylesXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <numFmts count="1">
    <numFmt numFmtId="164" formatCode="0.00"/>
  </numFmts>
  <fonts count="3">
    <font><sz val="11"/><color rgb="FF000000"/><name val="Calibri"/><family val="2"/></font>
    <font><sz val="18"/><color rgb="FF000000"/><name val="Calibri"/><family val="2"/></font>
    <font><b val="1"/><sz val="11"/><color rgb="FF000000"/><name val="Calibri"/><family val="2"/></font>
  </fonts>
  <fills count="6">
    <fill><patternFill patternType="none"/></fill>
    <fill><patternFill patternType="gray125"/></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FF${HEADER_FILL}"/><bgColor indexed="64"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FF${OTHERS_FILL}"/><bgColor indexed="64"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FF${UNMARKED_FILL}"/><bgColor indexed="64"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FF${NOTES_FILL}"/><bgColor indexed="64"/></patternFill></fill>
  </fills>
  <borders count="2">
    <border><left/><right/><top/><bottom/><diagonal/></border>
    <border>
      <left style="thin"><color rgb="FF000000"/></left>
      <right style="thin"><color rgb="FF000000"/></right>
      <top style="thin"><color rgb="FF000000"/></top>
      <bottom style="thin"><color rgb="FF000000"/></bottom>
      <diagonal/>
    </border>
  </borders>
  <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
  <cellXfs count="21">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
    <xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="0" fontId="2" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center" wrapText="1"/></xf>
    <xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center" wrapText="1"/></xf>
    <xf numFmtId="164" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="0" fontId="2" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="left" vertical="center"/></xf>
    <xf numFmtId="0" fontId="2" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="164" fontId="2" fillId="2" borderId="1" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="0" fontId="0" fillId="3" borderId="1" xfId="0" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center" wrapText="1"/></xf>
    <xf numFmtId="164" fontId="0" fillId="3" borderId="1" xfId="0" applyNumberFormat="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0" applyBorder="1" applyAlignment="1"><alignment horizontal="left" vertical="center" wrapText="1"/></xf>
    <xf numFmtId="164" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1" applyAlignment="1"><alignment horizontal="right" vertical="center"/></xf>
    <xf numFmtId="0" fontId="2" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="left" vertical="center"/></xf>
    <xf numFmtId="0" fontId="2" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="right" vertical="center"/></xf>
    <xf numFmtId="164" fontId="2" fillId="2" borderId="1" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="right" vertical="center"/></xf>
    <xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0" applyBorder="1" applyAlignment="1"><alignment horizontal="right" vertical="center"/></xf>
    <xf numFmtId="0" fontId="0" fillId="4" borderId="1" xfId="0" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center" wrapText="1"/></xf>
    <xf numFmtId="164" fontId="0" fillId="4" borderId="1" xfId="0" applyNumberFormat="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="0" fontId="0" fillId="5" borderId="1" xfId="0" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center" wrapText="1"/></xf>
    <xf numFmtId="164" fontId="0" fillId="5" borderId="1" xfId="0" applyNumberFormat="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
  </cellXfs>
</styleSheet>`;

const strCell = (ref: string, value: string, style: number) =>
  `<c r="${ref}" s="${style}" t="inlineStr"><is><t>${xmlEscape(value)}</t></is></c>`;

const numCell = (ref: string, value: number, style: number) =>
  `<c r="${ref}" s="${style}"><v>${value}</v></c>`;

const formulaCell = (
  ref: string,
  formula: string,
  cached: number,
  style: number
) =>
  `<c r="${ref}" s="${style}"><f>${xmlEscape(formula)}</f><v>${cached}</v></c>`;

const rowXml = (r: number, cells: string, height?: number) =>
  `<row r="${r}"${height ? ` ht="${height}" customHeight="1"` : ""}>${cells}</row>`;

const logoDrawingXml = () => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<xdr:wsDr xmlns:xdr="http://schemas.openxmlformats.org/drawingml/2006/spreadsheetDrawing" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main">
  <xdr:twoCellAnchor editAs="oneCell">
    <xdr:from><xdr:col>0</xdr:col><xdr:colOff>0</xdr:colOff><xdr:row>0</xdr:row><xdr:rowOff>0</xdr:rowOff></xdr:from>
    <xdr:to><xdr:col>2</xdr:col><xdr:colOff>574172</xdr:colOff><xdr:row>2</xdr:row><xdr:rowOff>276720</xdr:rowOff></xdr:to>
    <xdr:pic>
      <xdr:nvPicPr>
        <xdr:cNvPr id="1" name="Toothsketch Logo"/>
        <xdr:cNvPicPr><a:picLocks noChangeAspect="0"/></xdr:cNvPicPr>
      </xdr:nvPicPr>
      <xdr:blipFill>
        <a:blip xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" r:embed="rId1"/>
        <a:stretch><a:fillRect/></a:stretch>
      </xdr:blipFill>
      <xdr:spPr>
        <a:xfrm><a:off x="0" y="0"/><a:ext cx="1" cy="1"/></a:xfrm>
        <a:prstGeom prst="rect"><a:avLst/></a:prstGeom>
      </xdr:spPr>
    </xdr:pic>
    <xdr:clientData/>
  </xdr:twoCellAnchor>
</xdr:wsDr>`;

const buildSheetXml = (lab: GenerateBillLabSheet, payload: GenerateBillPayload) => {
  const isEd = lab.isElementDigital;
  const headerCols = isEd
    ? [
        "S.NO",
        "DATE",
        "CASE ID",
        "CASE NAME",
        "CATEGORY",
        "TOOTH NUMBER",
        "UNITS",
        "PRICE/UNIT",
        "TOTAL",
        "STATUS",
        "Units prev billed",
        "Pending units",
        "Lab Token",
      ]
    : [
        "S.NO",
        "DATE",
        "CASE ID",
        "CASE NAME",
        "CATEGORY",
        "TOOTH NUMBER",
        "UNITS",
        "PRICE/UNIT",
        "TOTAL",
      ];
  const colCount = headerCols.length;
  const lastCol = colLetter(colCount - 1);

  const categoryTotals = new Map<
    string,
    { units: number; total: number; ppu: number }
  >();
  for (const c of lab.cases) {
    const existing = categoryTotals.get(c.category) || {
      units: 0,
      total: 0,
      ppu: c.pricePerUnit,
    };
    existing.units += c.units;
    existing.total = round2(existing.total + c.total);
    existing.ppu = c.pricePerUnit;
    categoryTotals.set(c.category, existing);
  }

  const merges: string[] = [
    `D1:${lastCol}1`,
    `D2:${lastCol}2`,
    `D3:${lastCol}3`,
  ];

  const rows: string[] = [];

  // Title block (logo sits over A1:D3 via drawing)
  const titleCells = (row: number, text: string, style: number) =>
    Array.from({ length: colCount - 3 }, (_, i) => {
      const col = colLetter(i + 3); // D onward
      return strCell(`${col}${row}`, i === 0 ? text : "", style);
    }).join("");

  rows.push(rowXml(1, titleCells(1, "Work Summary", S.subtitle), 24));
  rows.push(
    rowXml(2, titleCells(2, `Lab Name: ${lab.invoiceName}`, S.subtitle), 24)
  );
  rows.push(
    rowXml(
      3,
      titleCells(3, `Month: ${payload.monthName}, ${payload.year}`, S.subtitle),
      24
    )
  );
  rows.push(rowXml(4, ""));

  // Table 1 header
  const headerRow = 5;
  rows.push(
    rowXml(
      headerRow,
      headerCols
        .map((label, i) => strCell(`${colLetter(i)}${headerRow}`, label, S.header))
        .join("")
    )
  );

  // Table 1 data
  const firstDataRow = 6;
  lab.cases.forEach((c, idx) => {
    const r = firstDataRow + idx;
    const unitsCol = colLetter(6);
    const ppuCol = colLetter(7);
    const totalCol = colLetter(8);
    const unmarked = c.unitsMarked === false;
    const hasNotes = c.hasUnitsNotes === true;
    const isOthers =
      String(c.category || "").trim().toLowerCase() === "others";
    // Priority: unmarked (red) > notes (green) > Others (yellow)
    const textStyle = unmarked
      ? S.cellUnmarked
      : hasNotes
        ? S.cellNotes
        : isOthers
          ? S.cellWarn
          : S.cell;
    const numStyle = unmarked
      ? S.cellNumUnmarked
      : hasNotes
        ? S.cellNumNotes
        : isOthers
          ? S.cellNumWarn
          : S.cellNum;
    const cells: string[] = [
      numCell(`${colLetter(0)}${r}`, idx + 1, textStyle),
      strCell(`${colLetter(1)}${r}`, c.date, textStyle),
      strCell(`${colLetter(2)}${r}`, c.caseId, textStyle),
      strCell(`${colLetter(3)}${r}`, c.caseName, textStyle),
      strCell(`${colLetter(4)}${r}`, c.category, textStyle),
      strCell(`${colLetter(5)}${r}`, c.toothNumbers, textStyle),
      numCell(`${unitsCol}${r}`, c.units, textStyle),
      numCell(`${ppuCol}${r}`, round2(c.pricePerUnit), numStyle),
    ];
    if (!isEd) {
      cells.push(
        formulaCell(
          `${totalCol}${r}`,
          `${unitsCol}${r}*${ppuCol}${r}`,
          round2(c.total),
          numStyle
        )
      );
    } else {
      cells.push(numCell(`${totalCol}${r}`, round2(c.total), numStyle));
      cells.push(strCell(`${colLetter(9)}${r}`, c.status, textStyle));
      cells.push(numCell(`${colLetter(10)}${r}`, c.unitsPrevBilled, textStyle));
      cells.push(numCell(`${colLetter(11)}${r}`, c.pendingUnits, textStyle));
      cells.push(strCell(`${colLetter(12)}${r}`, c.labToken || "N/A", textStyle));
    }
    rows.push(rowXml(r, cells.join("")));
  });

  const afterCases = firstDataRow + lab.cases.length;
  // Gap between the two tables
  rows.push(rowXml(afterCases, ""));

  const grandHeaderRow = afterCases + 1;
  merges.push(`A${grandHeaderRow}:${lastCol}${grandHeaderRow}`);
  rows.push(
    rowXml(
      grandHeaderRow,
      Array.from({ length: colCount }, (_, i) =>
        strCell(
          `${colLetter(i)}${grandHeaderRow}`,
          i === 0 ? "Grand Total" : "",
          S.grandHeader
        )
      ).join("")
    )
  );

  let grandUnits = 0;
  let grandTotal = 0;
  const categories = [...categoryTotals.entries()];
  const unitsCol = colLetter(6);
  const ppuCol = colLetter(7);
  const totalCol = colLetter(8);
  const firstCatRow = grandHeaderRow + 1;
  const lastCatRow = grandHeaderRow + categories.length;

  categories.forEach(([category, agg], idx) => {
    const r = firstCatRow + idx;
    // Wide left label (A:F) + UNITS / PRICE/UNIT / TOTAL on the right
    merges.push(`A${r}:F${r}`);
    const cells = Array.from({ length: colCount }, (_, i) => {
      const ref = `${colLetter(i)}${r}`;
      if (i === 0) return strCell(ref, category, S.cellLeft);
      if (i >= 1 && i <= 5) return strCell(ref, "", S.cellLeft);
      if (i === 6) return numCell(ref, agg.units, S.cellRight);
      if (i === 7) return numCell(ref, round2(agg.ppu), S.cellNumRight);
      if (i === 8) {
        return formulaCell(
          ref,
          `${unitsCol}${r}*${ppuCol}${r}`,
          round2(agg.total),
          S.cellNumRight
        );
      }
      return strCell(ref, "", S.cell);
    });
    rows.push(rowXml(r, cells.join("")));
    grandUnits += agg.units;
    grandTotal = round2(grandTotal + agg.total);
  });

  const resolvedFooterRow =
    categories.length > 0 ? lastCatRow + 1 : grandHeaderRow + 1;
  // A:E label | F Total | G units | H currency | I grand total
  merges.push(`A${resolvedFooterRow}:E${resolvedFooterRow}`);
  const hasCatRange = categories.length > 0;
  const footerCells = Array.from({ length: colCount }, (_, i) => {
    const ref = `${colLetter(i)}${resolvedFooterRow}`;
    if (i === 0) {
      return strCell(
        ref,
        `Grand total: ${payload.monthName}, ${payload.year}`,
        S.footerLeft
      );
    }
    if (i >= 1 && i <= 4) return strCell(ref, "", S.footerLeft);
    if (i === 5) return strCell(ref, "Total", S.footerRight);
    if (i === 6) {
      return hasCatRange
        ? formulaCell(
            ref,
            `SUM(${unitsCol}${firstCatRow}:${unitsCol}${lastCatRow})`,
            grandUnits,
            S.footerRight
          )
        : numCell(ref, 0, S.footerRight);
    }
    if (i === 7) return strCell(ref, lab.currency || "", S.footerRight);
    if (i === 8) {
      return hasCatRange
        ? formulaCell(
            ref,
            `SUM(${totalCol}${firstCatRow}:${totalCol}${lastCatRow})`,
            grandTotal,
            S.footerNumRight
          )
        : numCell(ref, 0, S.footerNumRight);
    }
    return strCell(ref, "", S.footer);
  });
  rows.push(rowXml(resolvedFooterRow, footerCells.join("")));

  const colsXml = headerCols
    .map((label, i) => {
      const width =
        i === 3 || i === 4 ? 28 : i === 5 ? 22 : Math.max(12, label.length + 2);
      return `<col min="${i + 1}" max="${i + 1}" width="${width}" customWidth="1"/>`;
    })
    .join("");

  const mergeXml = merges
    .map((ref) => `<mergeCell ref="${ref}"/>`)
    .join("");

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <cols>${colsXml}</cols>
  <sheetData>${rows.join("")}</sheetData>
  <mergeCells count="${merges.length}">${mergeXml}</mergeCells>
  <drawing r:id="rId1"/>
</worksheet>`;
};

const loadLogoBytes = async (): Promise<ArrayBuffer | null> => {
  try {
    const res = await fetch("/logoWhite.png");
    if (!res.ok) return null;
    return await res.arrayBuffer();
  } catch {
    return null;
  }
};

/** Build multi-sheet workbook matching Apps Script work-summary styling. */
export const buildGenerateBillWorkbook = async (
  payload: GenerateBillPayload
): Promise<Blob> => {
  const usedNames = new Set<string>();
  const logoBytes = await loadLogoBytes();
  const zip = new JSZip();

  const sheetFiles = payload.labs.map((lab, index) => {
    const name = sanitizeSheetName(lab.sheetName, usedNames);
    const sheetIndex = index + 1;
    return {
      name,
      sheetIndex,
      path: `xl/worksheets/sheet${sheetIndex}.xml`,
      xml: buildSheetXml(lab, payload),
    };
  });

  const workbookSheets = sheetFiles
    .map(
      (sheet) =>
        `<sheet name="${xmlEscape(sheet.name)}" sheetId="${sheet.sheetIndex}" r:id="rId${sheet.sheetIndex}"/>`
    )
    .join("");

  const workbookXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets>${workbookSheets}</sheets>
</workbook>`;

  const workbookRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId0" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
  ${sheetFiles
    .map(
      (sheet) =>
        `<Relationship Id="rId${sheet.sheetIndex}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${sheet.sheetIndex}.xml"/>`
    )
    .join("")}
</Relationships>`;

  const contentTypes = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Default Extension="png" ContentType="image/png"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
  ${sheetFiles
    .map(
      (sheet) =>
        `<Override PartName="/xl/worksheets/sheet${sheet.sheetIndex}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`
    )
    .join("")}
  ${
    logoBytes
      ? sheetFiles
          .map(
            (sheet) =>
              `<Override PartName="/xl/drawings/drawing${sheet.sheetIndex}.xml" ContentType="application/vnd.openxmlformats-officedocument.drawing+xml"/>`
          )
          .join("")
      : ""
  }
</Types>`;

  const rootRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;

  zip.file("[Content_Types].xml", contentTypes);
  zip.file("_rels/.rels", rootRels);
  zip.file("xl/workbook.xml", workbookXml);
  zip.file("xl/_rels/workbook.xml.rels", workbookRels);
  zip.file("xl/styles.xml", stylesXml);

  if (logoBytes) {
    zip.file("xl/media/logoWhite.png", logoBytes);
  }

  for (const sheet of sheetFiles) {
    // If logo missing, strip drawing reference from sheet XML
    const sheetXml = logoBytes
      ? sheet.xml
      : sheet.xml.replace(/\s*<drawing r:id="rId1"\/>/, "");
    zip.file(sheet.path, sheetXml);

    if (logoBytes) {
      zip.file(
        `xl/worksheets/_rels/sheet${sheet.sheetIndex}.xml.rels`,
        `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/drawing" Target="../drawings/drawing${sheet.sheetIndex}.xml"/>
</Relationships>`
      );
      zip.file(
        `xl/drawings/drawing${sheet.sheetIndex}.xml`,
        logoDrawingXml()
      );
      zip.file(
        `xl/drawings/_rels/drawing${sheet.sheetIndex}.xml.rels`,
        `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" Target="../media/logoWhite.png"/>
</Relationships>`
      );
    }
  }

  return zip.generateAsync({
    type: "blob",
    mimeType:
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};

export const downloadGenerateBillWorkbook = async (
  payload: GenerateBillPayload
) => {
  const blob = await buildGenerateBillWorkbook(payload);
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `Work_Summaries_${payload.year}_${String(payload.month).padStart(2, "0")}.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
